/**
 * Merkezi TR → EN çeviri sözlüğü — Hardcoded stringler için.
 *
 * useT() hook'u locale bağlamı okur:
 * - locale === "en" ve string burada varsa → EN karşılığını döndürür
 * - Aksi hâlde orijinal TR metni olduğu gibi bırakır
 *
 * Kullanım:
 *   const t = useT();
 *   <p>{t("Yuvasına Kavuştu")}</p>       // TR modunda "Yuvasına Kavuştu", EN modunda "Found Their Home"
 *
 * CMS'te olmayan (statik JSX içi) tüm ziyaretçi metinleri buraya taşınır.
 * Yeni EN dilinde bir metin çıkarsa: sadece bu dict'e satır ekle.
 */
import { useLanguage } from "@/context/LanguageContext";

// Anahtar = TR string, Değer = EN karşılığı
export const HARDCODED_EN = {
  // ============ Navigation ============
  "Ana Sayfa": "Home",
  "Canlarımız": "Our Companions",
  "PDF Rapor": "PDF Report",
  "Etkinlikler": "Events",
  "Şeffaflık": "Transparency",
  "Hakkımızda": "About Us",
  "Canları Keşfet": "Meet the Animals",
  "Bir Cana Destek Ol": "Support a Life",
  "Nasıl Çalışır?": "How It Works",
  "Pativista ile Birlikte": "In Partnership with Pativista",

  // ============ Landing – Hero ============
  '"Dokunduğumuz her can; kimliği, hikayesi ve ağı olan sahiplenilebilir bir karakter olarak görünür hale geliyor."':
    '"Every soul we touch becomes visible — as a character with identity, story, and community, ready to be embraced."',

  // ============ Landing – Stats ============
  "KAHU'da görünür kıldığımız can 🐾": "Lives we made visible on KAHU 🐾",
  "Yuvasına Kavuştu": "Found Their Home",
  "Güvende": "Safe & Sound",
  "Aramızdan Ayrıldı": "Passed On",
  "can aramızdan ayrıldı — anıyoruz": "souls have passed on — we remember",
  "Anında Destek": "Instant Support",
  "Genel Mama Desteği": "General Food Support",
  "Genel Veteriner Desteği": "General Vet Support",
  "Mama": "Food",
  "Veteriner": "Vet",
  "Anıları bizimle. Onlar için de mücadele ediyoruz.": "Their memories stay with us. We fight on for them, too.",
  "Ben de Sahiplenmek İstiyorum": "I Want to Adopt",
  "Sahiplenemiyorum, Destek Olayım": "I Can't Adopt — Let Me Support",
  "Aylık Destek": "Monthly Support",

  // ============ Landing – Bakım Süreci / Process ============
  "Sahiplendirme Süreci Nasıl İşliyor?": "How Does the Adoption Journey Work?",
  "Kayıp yok, hikaye tam — her aşama şeffaf ve KAHU-KOD üstünden izlenebilir.":
    "No details lost, full story told — each stage is transparent and trackable via KAHU-CODE.",
  "Sokak / İhbar": "Street / Report",
  "Buluşma": "Encounter",
  "Kimliklendirme": "Identification",
  "Sağlık Kontrolü": "Health Check",
  "Hikaye": "Storytelling",
  "Sahiplendirme": "Adoption",
  "Takip": "Follow-up",

  // ============ Landing – Türkiye Gerçeği / Turkey Reality ============
  "Türkiye'de Sokak Hayvanları Gerçeği": "The Reality of Street Animals in Turkey",
  "Rakamlar konuşuyor. Ama biz onları hikayelere çeviriyoruz.":
    "The numbers speak. But we turn them into stories.",
  "milyon sokak köpeği": "million street dogs",
  "yılda uyutulan hayvan": "animals euthanized each year",
  "belediye barınağı": "municipal shelters",
  "kapasitesinin üstünde": "over capacity",

  // ============ Landing – Nasıl Yardım Edebilirsin / Ways to Help ============
  "Nasıl Yardım Edebilirsin?": "How Can You Help?",
  "Küçük bir adım, kocaman bir fark yaratır.": "A small step makes a huge difference.",
  "Sahiplen": "Adopt",
  "Bir cana yuva ol. Ömürlük dost, ömürlük mutluluk.": "Give a soul a home. A lifelong friend, lifelong joy.",
  "Koruyucu Aile Ol": "Become a Guardian Family",
  "Uzaktan destekle. Aylık katkınla bir canın bakımını üstlen.":
    "Support from afar. With a monthly contribution, take on a soul's care.",
  "Bağış Yap": "Donate",
  "Mama, ilaç, veteriner masrafları — her destek can kurtarır.":
    "Food, medicine, veterinary costs — every contribution saves a life.",
  "Gönüllü Ol": "Volunteer",
  "Zamanınla katkı ver. Sosyal medya, saha ziyaretleri, etkinlikler.":
    "Contribute with your time. Social media, field visits, events.",

  // ============ Landing – Alt CTA ============
  "Küçük bir adım, kocaman bir fark.": "A small step, a huge difference.",
  "Hemen canlara bak, tanış, seç.": "See the animals, meet them, choose.",
  "Canları Gör": "View the Animals",

  // ============ Landing – Footer ============
  "Görünmez canları görünür kılıyoruz.": "Making invisible lives visible.",
  "İletişim": "Contact",
  "Çerez Ayarları": "Cookie Settings",
  "KVKK": "GDPR / Data Protection",
  "Gizlilik Politikası": "Privacy Policy",
  "Tüm hakları saklıdır": "All rights reserved",
  "KAHU - Tüm hakları saklıdır.": "KAHU — All rights reserved.",
  "Pati Bekçileri Derneği": "Paw Guardians Association",

  // ============ Dogs Page ============
  "Sana Bekleyen Canlar": "Companions Waiting for You",
  "Her biri özel, her biri eşsiz. Yeni en iyi dostunu bul.": "Each one is unique, each one is special. Find your new best friend.",
  "İsim, ırk veya mikroçip numarası ara...": "Search by name, breed, or microchip ID...",
  "Kart ile Düzenli Öde (Fonzip)": "Recurring Card Payment (Fonzip)",
  "Düzenli Desteğe Devam Et": "Continue to Recurring Support",
  "Hazırlanıyor...": "Preparing…",
  "Kart ile Öde (Fonzip)": "Pay by Card (Fonzip)",
  "{amount} TL Kartla Öde": "Pay {amount} TL by Card",
  "Bu ay için ilk ödeme alınır; sonraki aylar için ay başında kibarca hatırlatma e-postası göndereceğiz.": "The first payment is collected for this month; for the following months we'll send a gentle reminder email at the start of each month.",
  "Fonzip sayfasında tutarını ve kart bilgilerini gir (tek seferlik). Eşleştirme için aynı e-postayı kullan:": "On the Fonzip page, enter your amount and card details (one-time). Use the same email so we can match it:",
  "Fonzip'in güvenli bağış sayfasında kartla öde.": "Pay by card on Fonzip's secure donation page.",
  "Fonzip'in güvenli bağış sayfasına yönlendirileceksiniz. Kart bilgileriniz bizde saklanmaz.": "You'll be redirected to Fonzip's secure donation page. We never store your card details.",
  "Fonzip ile Kartla Öde →": "Pay by Card with Fonzip →",
  "Kart ile ödeme şu anda kullanılamıyor — banka havalesi/EFT ile devam edebilirsin.": "Card payment is unavailable right now — you can continue with bank transfer.",
  "Şu anda kullanılamıyor — banka havalesi/EFT ile devam edebilirsin.": "Unavailable right now — continue with bank transfer.",
  "Aylık {amount} TL — Fonzip ile Devam Et": "Continue with Fonzip — {amount} TL/mo",
  "Aylık destek tutarı": "Monthly support amount",
  "Minimum {amount} TL/ay. Sana uygun bir tutarı seçmekte özgürsün.": "Minimum {amount} TL/month. Feel free to pick an amount that works for you.",
  "Minimum tutar {amount} TL'dir.": "The minimum amount is {amount} TL.",
  "Aylık talimatını Fonzip'in güvenli sayfasında ver.": "Set up your monthly instruction on Fonzip's secure page.",
  "İsim veya ırk ara…": "Search name or breed…",
  "(Ücretsiz Başvuru)": "(Free Application)",
  "Ara": "Search",
  "Filtreler": "Filters",
  "Sahiplenmeye Hazır": "Ready for Adoption",
  "Bakımda": "In Care",
  "Barınakta": "In the Field",
  "Alanda": "In the Field",
  "Tedavi Görüyor": "In Treatment",
  "Transferde": "In Transit",
  "Beklemede": "Pending",
  "Karantinada": "In Quarantine",
  "Geçici Bakıcıda": "In Foster Care",
  "Sahiplendirildi": "Adopted",
  "Kayıp": "Missing",
  "Vefat": "Passed On",
  "Aramızdan Ayrıldı 🕊": "Passed On 🕊",
  "Transfer Edildi": "Transferred",
  "Sahiplendirme İşlemi Sürüyor": "Adoption in Progress",
  "Durum": "Status",
  "Boyut": "Size",
  "Cinsiyet": "Gender",
  "Tümü": "All",
  "Tümü (Açık Profiller)": "All (Open Profiles)",
  "🎉 Mutlu Hikayeler": "🎉 Happy Endings",
  "Dev": "Giant",
  "Kısa hikaye": "Short story",
  "Sakin ve uyumlu": "Calm and friendly",
  "Apartmana Uygun": "Apartment-Friendly",
  "Mutlu köpek": "Happy dog",
  "Mutlu köpekler birlikte": "Happy dogs together",
  "Son 12 Ay · Sahiplendirme": "Last 12 Months · Adoptions",
  "Kuğulu etkinlikleri ve dijital reach üzerinden gerçekleşen tüm yuvaya kavuşmalar.": "All homes found through Kuğulu events and digital reach.",
  "Son 12 Ay · Etkinlik": "Last 12 Months · Events",
  "Kuğulu Park ve diğer sahiplendirme etkinliği günleri.": "Kuğulu Park and other adoption event days.",
  "{n} can yuvasına kavuştu.": "{n} souls found a home.",
  "Etkinlik": "Event",
  "Aktif Koruyucu Aile": "Active Guardian Families",
  "Bir canı düzenli olarak destekleyen onaylı destekçi sayısı.": "The number of verified supporters who regularly support a soul.",
  "Son 30 Gün Yaşam Güncellemesi": "Life Updates (Last 30 Days)",
  "Pativista'nın canların profillerine düştüğü güncel sağlık, davranış ve yaşam notları.": "Latest health, behavior, and life notes Pativista added to companion profiles.",
  "Tamamlanan Sahiplendirme Başvurusu": "Completed Adoption Applications",
  "Onaylanmış ve süreci sonuçlanmış başvuru sayısı.": "Approved applications whose process has been completed.",
  "Rezerve": "Reserved",
  "İade": "Returned",
  "KAHU'nun değişim teorisinin sahadaki güncel karşılığı.": "KAHU's theory of change — updated with what's happening in the field.",
  "Her gösterge canlı, otomatik ve Pativista'nın saha kayıtlarından beslenir. Manuel veri girişi yapılmaz — sayılar Pativista admin panelindeki her aksiyonla anında yenilenir.": "Every indicator is live, automated, and drawn from Pativista's field records. No manual data entry — numbers refresh with every action in the Pativista admin panel.",
  "⚠️ Veri tutarsızlığı: kayıtlı toplam can ({total}) ile statülerin toplamı ({computed}) eşleşmiyor.": "⚠️ Data mismatch: registered total ({total}) does not match sum of statuses ({computed}).",
  "Sistemde Kayıtlı": "Registered in System",
  "KAHU'da dijital kimlik almış can sayısı.": "Number of souls with a digital identity on KAHU.",
  "Aktif Bakım Altında": "Under Active Care",
  "Alanda, geçici bakımda, sahiplenmeye hazır ya da tedavi gören canlar.": "Companions in the field, in temporary care, ready for adoption or under treatment.",
  "Kalıcı Yuvaya Kavuşan": "Reached a Permanent Home",
  "Sahiplendirilmiş ve yeni ailesiyle yaşamaya başlamış canlar.": "Souls who have been adopted and started life with a new family.",
  "Sen de gel — birlikte bir can yuva bulsun": "Come along — together, let's find a soul a home",
  "Önceden tanışmak istersen profiline göz at, etkinlikte yüz yüze tanış. Sahiplenmesen de gel — sevgi ver, fotoğraf çek, hikâyemizi yay.": "Browse their profile beforehand if you'd like to meet in advance, then meet in person at the event. Even if you can't adopt — come, give love, take photos, share our story.",
  "Canları Tanımaya Başla": "Start Meeting the Companions",
  "Pativista ile birlikte — KAHU dijital destek altyapısı": "In partnership with Pativista — powered by KAHU digital infrastructure",
  "Her isme dokun; hikayesini oku, hatırla.": "Tap each name — read their story, remember.",
  "Yayıncı": "Publisher",
  "Rapor canlı verilerden anlık üretilir": "Report generated live from real-time data",
  "Köpek Dostu": "Dog-Friendly",
  "Çocuk Dostu": "Kid-Friendly",
  "Filtreleri değiştirmeyi deneyin": "Try adjusting the filters",
  "Can Bulunamadı": "No Companions Found",
  "Temizle": "Clear",
  // DogsPage headers & CTAs
  "💛 Koruyucu Aile Arayan Canlar": "💛 Companions Seeking a Guardian Family",
  "🍲 Bekleyen Bir Cana Mama Desteği": "🍲 Food Support for a Waiting Soul",
  "⚕️ Bir Cana Veteriner Desteği": "⚕️ Vet Support for a Soul",
  "Seni Bekleyen Canlar": "Companions Waiting for You",
  "Bir cana birden fazla destekçi olabilir — kalabalıkken bile yer var 💛 Aşağıda mevcut destekçi sayısı az olanlar üstte sıralı.":
    "A soul can have multiple supporters — even in a crowd, there's room 💛 Below, those with fewer current supporters are listed first.",
  "Aşağıdan bir can seç ve mama desteğini doğrudan ona yönlendir. Bir tabak, somut bir fark.":
    "Choose a soul below and direct your food support straight to them. One plate, one tangible difference.",
  "Aşağıdan bir can seç ve veteriner desteğini onun sağlığına yönlendir. Aşı, kısırlaştırma, tedavi — hepsi sürüyor.":
    "Choose a soul below and direct vet support toward their health. Vaccinations, spay/neuter, treatment — all ongoing.",
  "Yuvasına kavuşan canlarımızın hikâyeleri. Her birinin kendi sevgi yolculuğu burada başladı.":
    "The stories of souls who found their homes. Each love journey began right here.",
  "Her biri benzersiz bir hikaye, her biri yeni bir başlangıç bekliyor":
    "Each one is a unique story, each one awaits a new beginning",
  "Bu hafta Kuğulu'da": "This week at Kuğulu",
  "Bu Cana Mama Desteği Ver": "Give This Soul Food Support",
  "Bu Cana Veteriner Desteği Ver": "Give This Soul Vet Support",
  "KAHU'nun katkı sunduğu sistemsel dönüşüm": "The systemic transformation KAHU contributes to",
  "Kimlikten görünürlüğe, görünürlükten kalıcı yuvaya: KAHU'nun bir canın yaşamında yarattığı değişim zinciri.":
    "From identity to visibility, from visibility to a permanent home: the change chain KAHU creates in a life.",
  "KAHU; her canı kimliklendirir, görünür kılar, doğru yuvayla buluşturur ve yaşam yolculuğunu şeffaf biçimde izler. Her aşama bir sonrakinin temelini oluşturur.":
    "KAHU identifies every soul, makes them visible, matches them with the right home, and transparently tracks their life journey. Each stage lays the foundation for the next.",
  "Girdiler": "Inputs",
  "Faaliyetler": "Activities",
  "Çıktılar": "Outputs",
  "Sonuçlar": "Outcomes",
  "Uzun Vadeli Etki": "Long-Term Impact",
  "Değişimi mümkün kılan kaynaklar": "The resources that make change possible",
  "Kaynakları eyleme dönüştüren süreçler": "Processes that turn resources into action",
  "Doğrudan ve ölçülebilir üretimler": "Direct and measurable outputs",
  "Kısa ve orta vadeli değişim": "Short- and medium-term change",
  "KAHU dijital altyapısı": "KAHU digital infrastructure",
  "Pativista'nın saha deneyimi": "Pativista's field experience",
  "Veteriner ve gönüllü ağı": "Vet and volunteer network",
  "Bağışçı ve koruyucu aile desteği": "Donor and guardian family support",
  "Güvenilir veri ve süreç standartları": "Reliable data and process standards",
  "Her cana dijital kimlik ve profil oluşturma": "Creating a digital identity and profile for every soul",
  "Sağlık, davranış ve bakım bilgilerini güncelleme": "Updating health, behavior, and care information",
  "Sahiplendirme başvurularını yapılandırılmış biçimde yönetme": "Managing adoption applications in a structured way",
  "Bağış ve koruyucu aile eşleşmeleri oluşturma": "Creating donation and guardian family matches",
  "Sahiplendirme sonrası takip ve yaşam güncellemeleri paylaşma": "Post-adoption follow-up and sharing life updates",
  "Kayıtlı ve güncel can profilleri": "Registered and up-to-date profiles",
  "Tamamlanan sahiplendirme başvuruları": "Completed adoption applications",
  "Gerçekleşen sahiplendirmeler": "Realized adoptions",
  "Aktif koruyucu aile ve düzenli destekçiler": "Active guardian families and recurring supporters",
  "Paylaşılan yaşam ve sağlık güncellemeleri": "Shared life and health updates",
  "Canların görünürlüğü ve doğru eşleşme ihtimali artar": "Visibility of lives and probability of the right match increase",
  "Sahiplendirme süresi ve iade oranı azalır": "Adoption duration and return rate decrease",
  "Personelin manuel takip yükü hafifler": "Manual tracking burden on staff is reduced",
  "Düzenli destek geliri artar": "Recurring support income grows",
  "Veri güvenilirliği ve kamu güveni güçlenir": "Data reliability and public trust strengthen",
  "Daha fazla can güvenli ve kalıcı yuvaya ulaşır": "More lives reach safe and permanent homes",
  "Kayıp, ihmal ve açıklanamayan ölüm riski azalır": "Risk of loss, neglect, and unexplained death decreases",
  "Yaşam alanları finansal olarak daha sürdürülebilir hale gelir": "Sanctuaries become financially more sustainable",
  "Hayvan refahı ölçülebilir ve hesap verebilir olur": "Animal welfare becomes measurable and accountable",
  "Model diğer yaşam alanlarına ve belediyelere yayılır": "The model spreads to other sanctuaries and municipalities",
  // Common dog description hardcoded fallback
  "Şöyle oldu, böyle oldu blablablabalabala...": "This happened, that happened, blah blah blah...",
  "transfer": "transfer",
  "Karışık": "Mixed Breed",
  // Pativista section
  "Pativista'dan": "From Pativista to a",
  "Ölçeklenebilir": "Scalable",
  "Modele": "Model",
  "Pativista gibi yüksek standartlı yaşam alanları model üretecek kapasitede. KAHU ile onların sahada başardığını dijital ile güçlendirerek sürdürülebilir ve ölçeklenebilir bir çözüm yaratıyoruz.":
    "Pativista's high-standard sanctuaries have the capacity to become a replicable model. With KAHU, we amplify their field success through digital tools, creating a sustainable and scalable solution.",
  "Hayvanlar için": "For the Animals",
  "İsimsiz kalmak yerine, kimliği, hikâyesi ve ağı olan bireyler yaratıyoruz": "Instead of remaining nameless, we create individuals with identity, story, and community",
  "Gönüllüler için": "For Volunteers",
  "WhatsApp kaosundan çıkıp düzenli ve kayıtlı bir sisteme geçiyoruz": "We move from WhatsApp chaos to an organized, documented system",
  "Toplum için": "For Society",
  "Şeffaf, takip edilebilir ve güven yaratan bir yapı kuruyoruz": "We build a transparent, traceable, and trust-generating structure",
  "Yuva bulan can": "Souls that found a home",
  // Change Starts With You section
  "can yuva buldu": "souls found homes",
  "can güvende": "souls are safe",
  // Footer & tail
  "Gizlilik": "Privacy",
  "Yönetim": "Admin",
  "Kangal": "Kangal",
  "Golden Mix": "Golden Mix",
  "İspanyol Cocker": "Spanish Cocker",
  "Küçük": "Small",
  "Orta": "Medium",
  "Büyük": "Large",
  "Erkek": "Male",
  "Dişi": "Female",

  // ============ Transparency Page ============
  "Şeffaflık ve Etki Raporu": "Transparency and Impact Report",
  "Şeffaflık ve": "Transparency and",
  "Etki": "Impact",
  "Raporu": "Report",
  "PDF İndir": "Download PDF",
  "Sistemdeki Can": "Lives in the System",
  "Sistemdeki Canlar": "Lives in the System",
  "Sahiplendirilen": "Adopted",
  "Etkinlik (son 12 ay)": "Events (last 12 months)",
  "THEORY OF CHANGE · IRIS+-COMPLIANT · ALIGNED WITH UN SDGS": "THEORY OF CHANGE · IRIS+-COMPLIANT · ALIGNED WITH UN SDGS",
  "INPUTS": "INPUTS",
  "ACTIVITIES": "ACTIVITIES",
  "OUTPUTS": "OUTPUTS",
  "OUTCOMES": "OUTCOMES",
  "LONG-TERM IMPACT": "LONG-TERM IMPACT",
  "Sahiplendirme süresi ve iade oranı": "Adoption duration and return rate",

  // ============ Contact & Legal ============
  "Bize Ulaşın": "Get in Touch",
  "Adres": "Address",
  "Telefon": "Phone",
  "E-posta": "Email",
  "Sosyal Medya": "Social Media",
  "Mesajınız": "Your Message",
  "Gönder": "Send",

  // ============ Common CTA ============
  "Devam Et": "Continue",
  "İptal": "Cancel",
  "Kaydet": "Save",
  "Kapat": "Close",
  "Geri": "Back",
  "İleri": "Next",
  "Tamam": "OK",
  "Evet": "Yes",
  "Hayır": "No",
  // Contact page
  "Bize Ulaş": "Get in Touch",
  "Bir sorunuz, geri bildiriminiz veya işbirliği teklifiniz mi var? Yazın, en kısa sürede dönelim.":
    "Have a question, feedback, or partnership proposal? Write to us — we'll get back to you soon.",
  "Mesajınız iletildi 🐾": "Your message has been delivered 🐾",
  "Yazdıklarınız ekibimize ulaştı.": "Your message reached our team.",
  "adresinden en kısa sürede dönüş yapacağız.": "we'll get back to you as soon as possible.",
  "Ana Sayfaya Dön": "Back to Home",
  "Ad Soyad *": "Full Name *",
  "Adınız ve soyadınız": "Your first and last name",
  "E-posta *": "Email *",
  "Konu": "Subject",
  "Mesajınızın konusu (opsiyonel)": "Subject of your message (optional)",
  "Mesajınız *": "Your Message *",
  "Yazmak istediklerinizi paylaşın...": "Share what's on your mind...",
  "Gönderiliyor...": "Sending...",
  "Mesajı Gönder": "Send Message",
  // Common: mama button on ApplicationSuccess
  "Mama Desteği vermek istiyorum": "I want to give food support",
  "Yükleniyor...": "Loading...",
  "Henüz Kayıt Yok": "No Records Yet",
  "Daha Fazla": "More",

  // ============ Application Success Page ============
  "can dostunun": "your companion",
  "Başvurun alındı!": "Your application has been received!",
  "İyi ki varsın! Desteğin alındı.": "Thank you for being here — your support has arrived.",
  "Biz başvurunu değerlendirirken, {name}'nı yalnız bırakma.": "While we review your application, don't leave {name} on their own.",
  "İstersen bugün küçük bir destekle {name}'nın veteriner masrafına katkı olabilirsin. Bu destek doğrudan fark yaratır.": "With a small contribution today, you can help cover {name}'s vet care. This support makes a direct difference.",
  "Karnı doysun": "So they don't go hungry",
  "Sağlığına destek ol": "Support their health",
  "Aşağıdaki tutarlar temsilidir; gerçek maliyetler bulunduğun şehir ve döneme göre değişebilir.": "Amounts below are indicative; actual costs may vary by city and season.",
  "Aşağıdaki tutarlar temsilidir; gerçek veteriner masrafları kliniğe ve tedaviye göre değişebilir.": "Amounts below are indicative; actual vet costs vary by clinic and treatment.",
  "1.900₺ = 1 veteriner ziyareti": "₺1,900 = 1 vet visit",
  "Veteriner Desteği Ol": "Support Vet Care",
  "İstersen daha küçük bir katkı da seçebilirsin.": "You can also choose a smaller contribution.",
  "(90₺ = 1 gün mama)": "(₺90 = 1 day of food)",
  "Genellikle <strong>24 saat</strong> içinde dönüş yapıyoruz; yoğunlukta <strong>1 haftaya</strong> uzayabilir.": "We usually get back within <strong>24 hours</strong>; during busy periods it may take up to <strong>1 week</strong>.",
  "Randevusuz ziyaret kabul edilmez.": "Visits without an appointment are not accepted.",
  "Seçtiğiniz kanaldan (WhatsApp/Instagram) yazacağız.": "We'll write to you through your chosen channel (WhatsApp/Instagram).",
  "<strong>Uyum dönemi normaldir:</strong> İlk 3 gün zorlayıcı olabilir; ilk 3 haftada rutin oturur; ilk 3 ayda tam uyum gerçekleşir. Bu süreçte destek veririz.": "<strong>An adjustment period is normal:</strong> The first 3 days may be challenging; a routine settles in the first 3 weeks; full adjustment happens within the first 3 months. We'll support you throughout.",
  "Başvuru No:": "Application No:",
  "Diğer Canları Gör": "See Other Companions",

  // ============ Dog Profile Page ============
  "Irk:": "Breed:",
  "Doğum:": "Born:",
  "Bilinmiyor": "Unknown",
  "Çocuklarla Uyumlu": "Good with Children",
  "Köpeklerle Uyumlu": "Good with Dogs",
  "Hikayesi": "Their Story",
  "Bu can için henüz bir hikaye yazılmamış.": "No story has been written for this soul yet.",
  "Cinsiyet:": "Gender:",
  "Boyut:": "Size:",
  "Hakkında": "About",
  "Güncellemeler": "Updates",
  "Yorumlar": "Comments",
  "Sağlık": "Health",
  "Zaman": "Timeline",
  "Özellikler": "Traits",
  "Geliş Tarihi": "Intake Date",
  "Son Güncellemeler": "Latest Updates",
  "Henuz guncelleme paylasilmamis.": "No updates have been shared yet.",
  "Güncelleme": "Update",
  "Toplam {n} guncelleme mevcut.": "Total {n} updates available.",
  "Sağlık Onayları": "Health Confirmations",
  "{n} hayvan sever bu canın sağlığını onayladı": "{n} animal lovers have confirmed this soul's health",
  "Yorum Yaz": "Write a Comment",
  "Adınız": "Your Name",
  "Örn. Ayşe Y.": "e.g. Ayse Y.",
  "{name} hakkında bir yorum yazın... Ziyaret ettiniz mi? Nasıl görünüyordu?": "Write a comment about {name}... Have you visited? How did they look?",
  "Bu canın sağlığını onaylıyorum (yakın zamanda gördüm)": "I confirm this soul's health (I saw them recently)",
  "Henüz yorum yok": "No comments yet",
  "Bu can hakkında ilk yorumu siz yapın!": "Be the first to comment on this soul!",
  "Doğrulanmış": "Verified",
  "Sağlık Onayı": "Health Confirmed",
  "Videolar": "Videos",
  "Sağlık Durumu": "Health Status",
  "Kısırlaştırma": "Spay/Neuter",
  "Kısırlaştırıldı": "Spayed/Neutered",
  "Kısırlaştırılmadı": "Not Spayed/Neutered",
  "Aşı Durumu": "Vaccination Status",
  "Tam": "Complete",
  "Eksik": "Incomplete",
  "Yapılmadı": "Not Done",
  "Parazit Tedavisi": "Parasite Treatment",
  "Yapıldı": "Done",
  "Kilo": "Weight",
  "kg": "kg",
  "Aşı Detayları": "Vaccination Details",
  "Sağlık Notları": "Health Notes",
  "Veteriner:": "Veterinarian:",
  "Sağlık Kayıtları": "Health Records",
  "Henüz sağlık bilgisi girilmedi": "No health information entered yet",
  "Ek sağlık kaydı bulunmuyor": "No additional health records",
  "Zaman Çizelgesi": "Timeline",
  "Henüz olay kaydı yok": "No timeline events yet",
  "'e ": " · ",
  "Önerilen": "Recommended",
  "TL": "TL",
  "TL Bağış Yap": "TL Donate",
  "Veya özel tutar girin (min 100 TL)": "Or enter a custom amount (min 100 TL)",
  "Koruyucu": "Guardian",
  "Bağış": "Donate",
  "Bu Can Profilini paylaş, sesimizi duyur!": "Share this profile — help us be heard!",
  "paylaş": "share",
  "WhatsApp'ta paylaş": "Share on WhatsApp",
  "X (Twitter) üzerinden": "Share via X (Twitter)",
  "Facebook'ta paylaş": "Share on Facebook",
  "Instagram Story için indir": "Download for Instagram Story",
  "Markalı 1080×1920 görsel": "Branded 1080×1920 image",
  "Instagram Feed için indir": "Download for Instagram Feed",
  "Markalı 1080×1080 görsel": "Branded 1080×1080 image",
  "Instagram DM'e link": "Link for Instagram DM",
  "Link kopyala + IG aç": "Copy link + open IG",
  "Bağlantıyı kopyala": "Copy link",
  "Cihaz paylaşımı": "Device share",
  "Metin kopyalandı! Instagram DM veya story'de yapıştırabilirsin.": "Text copied! You can paste it in an Instagram DM or story.",
  "Bu bağlantıyı kopyalayın ve Instagram'da paylaşın:": "Copy this link and share it on Instagram:",
  "Kopyalanamadı — bağlantıyı manuel paylaşın.": "Couldn't copy — share the link manually.",
  "Story görseli indirildi. Instagram Story'e yükle, link sticker ekle → paylaş!": "Story image downloaded. Upload to Instagram Story, add a link sticker → share!",
  "Feed görseli indirildi. Instagram post olarak paylaş, açıklamaya bağlantıyı yapıştır.": "Feed image downloaded. Share as an Instagram post, paste the link in the caption.",
  "Görsel indirilemedi. Bu can için fotoğraf gerekli olabilir.": "Couldn't download image. A photo may be required for this soul.",
  "Bağlantı kopyalandı!": "Link copied!",
  "Bu bağlantıyı kopyalayın:": "Copy this link:",
  "Kopyalanamadı, adresi manuel kopyalayın.": "Couldn't copy — copy the address manually.",
  "Koruyucu Ailesi Var": "Has a Guardian Family",
  "Bu can": "This companion",
  "'i tanıyın! ": " — meet them! ",
  "'i tanıyın!": " — meet them!",
  "Sahiplenmeyi bekliyor.": "Awaiting adoption.",
  "Lütfen bir yorum yazın": "Please write a comment",
  "Lütfen adınızı yazın": "Please enter your name",
  "Sağlık onayınız eklendi!": "Your health confirmation has been added!",
  "Yorumunuz eklendi!": "Your comment has been added!",
  "Yorum eklenemedi": "Couldn't add comment",
  "Bağışınız için teşekkür ederiz!": "Thank you for your donation!",
  "Bağış işlemi başarısız": "Donation failed",
  "Lütfen bir resim dosyası seçin": "Please select an image file",
  "Dosya boyutu 5MB'dan küçük olmalı": "File size must be under 5MB",
  "Fotoğraf yüklendi!": "Photo uploaded!",
  "Fotoğraf yüklenemedi": "Couldn't upload photo",

  // ============ NotFound Page ============
  "Bu patika kayıp gitmiş": "This trail seems to have wandered off",
  "Aradığınız sayfayı bulamadık. Belki bir yazım hatası var, belki de bu can başka bir hikâyeye taşınmış. Aşağıdan geri dönebilir ya da köpekleri keşfetmeye başlayabilirsiniz.": "We couldn't find the page you're looking for. Maybe it's a typo, or this soul has moved on to another story. Head back home or start exploring our companions.",
  "Ana Sayfa'ya Dön": "Back to Home",

  // ============ Donation Success Page ============
  "Teşekkürler!": "Thank You!",
  "Bağışınız başarıyla alındı. Kimsesiz canlara uzanan eliniz için çok teşekkür ederiz.": "Your donation has been received. Thank you for reaching out a hand to souls in need.",
  "Ödeme Başarısız": "Payment Failed",
  "Ödeme işlemi tamamlanamadı. Lütfen tekrar deneyin.": "Payment couldn't be completed. Please try again.",

  // ============ Application Completed Page ============
  "Başvurunuz Alındı!": "Your application has been received!",
  "için başvurunuz kaydedildi.": ": your application has been saved.",
  "Bundan Sonra Ne Olacak?": "What Happens Next?",
  "Genellikle 24 saat içinde dönüş yapıyoruz.": "We usually respond within 24 hours.",
  "İletişime geçileceğiz": "We'll reach out",
  "WhatsApp üzerinden": "via WhatsApp",
  "Instagram üzerinden": "via Instagram",
  "Tanışma görüşmesi": "Meet-and-greet",
  "Online görüşme planlanacak": "An online meeting will be scheduled",
  "Yüz yüze görüşme planlanacak": "An in-person meeting will be scheduled",
  "Önemli Hatırlatmalar": "Important Reminders",
  "• Yoğun dönemlerde dönüş 1 haftaya uzayabilir.": "• During busy periods, response may take up to 1 week.",
  "• Randevusuz ziyaret kabul edilmez.": "• Walk-in visits are not accepted.",
  "• Lütfen iletişim bilgilerinizi açık tutun.": "• Please keep your contact info reachable.",
  "Başvuru Numaranız": "Your Application Number",

  // ============ Mobile Nav ============
  "Menüyü aç": "Open menu",
  "Ana Menü": "Main Menu",
  "Pativista ile birlikte": "In partnership with Pativista",

  // ============ Events Page ============
  "Pativista Sahiplendirme Günü · KAHU destekli": "Pativista Adoption Day · Powered by KAHU",
  "Kuğulu Park'ta": "At Kuğulu Park",
  "Buluşalım": "Let's Meet",
  "Her hafta bir avuç gönüllü, onlarca can ve sevgi dolu bir park. Gel tanış — çocuklar köpeklerle oynasın, sen yeni dostunla tanış, biz birlikte kimsesiz tek bir can kalmayana dek mücadele verelim 💪": "Every week: a handful of volunteers, dozens of souls, and a park full of love. Come meet them — let kids play with dogs, meet your new best friend, and together we'll fight until no soul is left alone 💪",
  "Yükleniyor…": "Loading…",
  "Yaklaşan Etkinlikler": "Upcoming Events",
  "Şu an duyurulmuş bir etkinlik yok. Bizi takip etmeye devam et — bir sonraki Pazar yine Kuğulu'dayız.": "No announced events right now. Stay tuned — next Sunday we're back at Kuğulu.",
  "Bir sonraki buluşma Instagram'da duyurulur": "The next meet-up is announced on Instagram",
  "Pativista, sahiplendirme günlerini kendi Instagram sayfasından paylaşıyor. Tarih netleştiği anda ilk oradan duyurulur — takipte kal, biz de burada canları ve sonuçları paylaşmaya devam edeceğiz.": "Pativista announces its adoption days on their own Instagram page. The moment a date is set, it goes up there first — follow along, and we'll keep sharing the souls and the outcomes here.",
  "Pativista'yı Instagram'da Takip Et": "Follow Pativista on Instagram",
  "Yuva Bekleyen Canları Gör": "See Souls Waiting for a Home",
  "Geçmiş Etkinlikler": "Past Events",
  "Geçmiş Etkinliklerden Kareler": "Moments from Past Events",
  "an": "moments",
  "Her Pazar bir araya geldiğimiz Kuğulu Park'tan kareler. Üstüne gel — o anın hangi etkinlikten olduğunu gör; tıkla — büyüt.": "Frames from Kuğulu Park where we gather every Sunday. Hover to see which event; click to zoom in.",
  "Bugün!": "Today!",
  "Yaklaşan": "Upcoming",
  "can": "companions",
  "seni bekliyor": "waiting for you",
  "Haritada Aç": "Open in Maps",
  "Detay": "Details",
  "ziyaretçi": "visitors",
  "TL bağış": "TL raised",

  // ============ Event Detail Page ============
  "Etkinlik bulunamadı.": "Event not found.",
  "Etkinliklere Dön": "Back to Events",
  "Tüm Etkinlikler": "All Events",
  "Geçmiş Etkinlik": "Past Event",
  "Tarih": "Date",
  "Lokasyon": "Location",
  "Google Maps'te aç": "Open in Google Maps",
  "Etkinlikten": "From the Event",
  "Ziyaretçi": "Visitors",
  "Başvuru": "Applications",
  "Toplanan bağış": "Donations raised",
  "Etkinlikteki Canlar": "Companions at the Event",
  "Etkinlikten Kareler": "Photos from the Event",

  // ============ Guardian Updates Page ============
  "Koruyucu aile olarak {name}'nin son durumunu buradan takip edebilirsiniz.": "As a guardian family, you can follow {name}'s latest updates here.",
  "Sayfa bulunamadi veya link gecersiz.": "Page not found or the link is invalid.",
  "Koruyucu ailelere ozel": "Guardians only",
  "Koruyucu Platform": "Guardian Platform",

  // ============ DogsPage extras ============
  "Bu hafta {n} can {loc}'ta — etkinliği gör": "This week {n} companions at {loc} — see the event",

  // ============ Transparency extras ============
  "Veriler yüklenemedi.": "Data could not be loaded.",
  "En Yoğun Ay": "Peak Month",
  "Toplam Kayıt": "Total Records",
  "Pativista Pilotunun Anlık Görünümü": "Pativista Pilot — Live Snapshot",
  "Ana Etki Göstergeleri": "Key Impact Indicators",
  "Diğer Statüler": "Other Statuses",
  "Vefat, kayıp, transfer ve diğer durumlar — detay için hover.": "Passed on, missing, transferred and other statuses — hover for details.",
  "Statü Dağılımı": "Status Distribution",
  "Bu kategoride henüz kayıt yok.": "No records in this category yet.",
  "gün (medyan)": "days (median)",
  "Hastalık": "Illness",
  "Kaza": "Accident",
  "Yaşlılık": "Old Age",
  "Kaçış": "Escape",
  "Çalınma şüphesi": "Suspected theft",
  "Diğer": "Other",


  // ============ Adoption/Guardian Forms + Donation Modal ============
  "(makbuz/teşekkür için)": "(for receipt / thank-you)",
  "(opsiyonel — başvurun ilerledikçe sana mail göndereceğiz)": "(optional — we'll email you as your application progresses)",
  "1. Şehir / İlçe / Semt": "1. City / District / Neighborhood",
  "15 günde bir": "Every 15 days",
  "2. Yaşınız": "2. Your Age",
  "25+ önceliğine rağmen neden değerlendirilmek istiyorsunuz?": "Despite the 25+ priority, why do you want to be considered?",
  "3. Görüşme tercihiniz": "3. Meeting preference",
  "4. İletişim tercihiniz": "4. Contact preference",
  "5. Yaşam alanına randevusuz gelebileceğinizi düşünüyor musunuz?": "5. Do you think you might drop by our living space without an appointment?",
  "6 kısa soru": "6 short questions",
  "6. Köpek günde kaç saat yalnız kalacak?": "6. How many hours a day will the dog be alone?",
  "90₺ = 1 gün mama": "90₺ = 1 day of food",
  "Adınız Soyadınız": "Full Name",
  "Ankara dışı başvurular ikinci sırada değerlendirilir.": "Out-of-Ankara applications are considered second.",
  "Ankara dışından sahiplendirme mümkündür.": "Adoption from outside Ankara is possible.",
  "Aylık Destek Tutarı": "Monthly Support Amount",
  "Aylık desteğini başlatmak için bir yöntem seç:": "Choose a method to start your monthly support:",
  "Açıklama kodu": "Description code",
  "Açıklamanız...": "Your explanation...",
  "Aşılı ve kısır": "Vaccinated and spayed/neutered",
  "Aşısız veya kısır değil": "Not vaccinated or spayed/neutered",
  "Bahçe": "Garden",
  "Bahçeli": "With Garden",
  "Bakamayacağınızı anlarsanız köpeği": "If you realize you can't care for the dog,",
  "Banka Havalesi / EFT ile Aylık Destek": "Monthly Support via Bank Transfer / EFT",
  "Banka Havalesi / EFT ile Bağış": "Donate via Bank Transfer / EFT",
  "Banka adı": "Bank name",
  "Banka uygulamandan düzenli havale talimatı verebilirsin.": "You can set up a recurring transfer from your banking app.",
  "Bağ kurma ve tam uyum gerçekleşir.": "Bonding and full adjustment happen.",
  "Bağışını hızlıca tamamla.": "Complete your donation quickly.",
  "Başlamadan Önce: Önemli Bilgiler": "Before You Start: Important Information",
  "Başvurular değerlendirilir ve uygun görülen kişilerle WhatsApp / Instagram üzerinden iletişime geçilir.": "Applications are reviewed and suitable applicants are contacted via WhatsApp / Instagram.",
  "Başvurunuz alındı!": "Your application has been received!",
  "Bina yönetimi kısıtı var mı?": "Is there a building-management restriction?",
  "Bu süreçte destek veririz; acele karar vermeyin.": "We support you throughout — don't rush the decision.",
  "Daha önce köpek baktım": "I've cared for a dog before",
  "Daha önce sahiplendirme deneyiminiz var mı? Ne oldu?": "Do you have prior adoption experience? What happened?",
  "Dernek adı ve hesap ismi": "Association name & account holder",
  "Desteğini istediğin zaman durdurabilirsin.": "You can stop your support anytime.",
  "Devletin çipli köpeğin kaybı vb. durumlarda ceza uygulayabildiği bilgisi vardır (": "The government may fine you in cases like the loss of a microchipped dog (",
  "Diğer Canları Keşfet": "Discover Other Companions",
  "En az birini seçin:": "Select at least one:",
  "Ev Halkı": "Household",
  "Ev içi": "Indoors",
  "Evde Başka Hayvan": "Other Pets at Home",
  "Evde başka köpek var": "Another dog at home",
  "Evde hayvan alerjisi olan var mı?": "Does anyone at home have a pet allergy?",
  "Evde kaç kişi yaşıyor?": "How many people live at home?",
  "Evde yaşayan herkes köpek istiyor mu?": "Does everyone at home want a dog?",
  "Evde çocuk var": "There are children at home",
  "Geri dönüş": "Response time",
  "Günde kaç kez yürüyüş?": "How many walks per day?",
  "Günlük Hayat ve Bakım Planı": "Daily Life & Care Plan",
  "Güvenilir referansım var": "I have a trusted reference",
  "Hayvan bakımında yaşadığınız zorluklar ve çözümler...": "Difficulties you've faced in caring for animals and your solutions...",
  "Hızlı Uygunluk Kontrolü": "Quick Eligibility Check",
  "Hızlı katkı seç": "Pick a quick contribution",
  "Hızlı, kolay ve anında onay.": "Fast, easy, instant approval.",
  "Instagram kullanıcı adınız": "Your Instagram username",
  "Kararsız": "Undecided",
  "Kart ile ödeme yakında aktif olacak. Lütfen banka havalesi/EFT kullanın.": "Card payments will be live soon. Please use bank transfer / EFT.",
  "Kayıt oluşturulamadı": "Record couldn't be created",
  "Kişi sayısı": "Number of people",
  "Koruyucu Aile (Aylık Destek)": "Guardian Family (Monthly Support)",
  "Koruyucu Aile kaydı bulunamadı": "Guardian Family record not found",
  "Köpek Pativista'ya iade edilir; \"bakamazlarsa köpek bizim köpeğimiz, geri alıyoruz\" kuralı geçerlidir.": "The dog is returned to Pativista; the rule \"if they can't care, the dog is ours — we take them back\" applies.",
  "Köpek bilgisi alınamadı": "Couldn't load dog info",
  "Köpek evde nerede yaşayacak?": "Where will the dog live at home?",
  "Köpek stres/şaşkınlık yaşayabilir; iştahsızlık, çekingenlik ve huzursuzluk görülebilir.": "The dog may experience stress/confusion; loss of appetite, shyness, and restlessness can occur.",
  "Lütfen dikkatlice okuyun": "Please read carefully",
  "Lütfen havale açıklamasına bu kodu yazın. Böylece bağışınızı hızlıca eşleştiririz.": "Please write this code in the transfer description so we can match your donation quickly.",
  "Mevcut hayvanların aşı ve kısırlaştırma durumu": "Vaccination & spay/neuter status of your existing pets",
  "Motivasyonunuzu açıklayın...": "Explain your motivation...",
  "Müstakil": "Detached house",
  "Onay Kutuları (zorunlu)": "Confirmation Boxes (required)",
  "Referans adı ve iletişim bilgisi": "Reference name & contact",
  "Rutin oturmaya başlar; gerçek karakteri ve ihtiyaçları daha net görünür.": "Routine settles in; the true character and needs become clearer.",
  "Sahiplendirme başvurusu doldurulur.": "Adoption application is filled out.",
  "Sahiplendirme sonrası": "After adoption",
  "Sayı": "Number",
  "Seçin": "Select",
  "Son adım": "Last step",
  "Sorumluluğu nasıl karşılayacaksınız? (zaman, bütçe, düzen)": "How will you meet the responsibility? (time, budget, structure)",
  "Süreç nasıl işler?": "How does the process work?",
  "Taahhütler ve Onay": "Commitments & Consent",
  "Tanıştırma planınız nedir?": "What is your introduction plan?",
  "Telefon numaranız": "Your phone number",
  "Toplam yürüyüş süresi": "Total walking time",
  "Uygun eşleşme olursa teslim süreci başlar.": "If there's a suitable match, the handover process begins.",
  "Uygun saat aralığı (opsiyonel)": "Preferred time range (optional)",
  "Uyum dönemi normaldir (3 gün – 3 hafta – 3 ay)": "An adjustment period is normal (3 days – 3 weeks – 3 months)",
  "Varsa kısaca anlatın...": "If applicable, briefly describe...",
  "Veya özel tutar girin:": "Or enter a custom amount:",
  "Yaşam Koşulları": "Living Conditions",
  "Yaşınızı girin": "Enter your age",
  "Geçici Bakımda": "In Temporary Care",
  "Destekçisi Var": "Has Sponsors",
  "Detaylı Başvuru": "Detailed Application",
  "Ödeme sayfası açılıyor...": "Opening the payment page...",
  "Bağışın can dostlarımızın bakımına gider.": "Your donation goes to the care of our companions.",
  "Bağışın mama bütçesine katkı olacak.": "Your donation contributes to the food budget.",
  "Bağışın veteriner bütçesine katkı olacak.": "Your donation contributes to the veterinary budget.",
  "Ödeme Yöntemini Değiştir": "Change Payment Method",
  "Desteğin için teşekkürler. Yuva bekleyen diğer canlara da göz atmak ister misin?": "Thank you for your support. Would you like to meet other companions still waiting for a home?",
  "Mama desteğin alındı. Teşekkürler!": "Your meal support has been received. Thank you!",
  "Veteriner desteğin alındı. Teşekkürler!": "Your veterinary support has been received. Thank you!",
  "{name} ve arkadaşları için mama desteği ver.": "Support meals for {name} and friends.",
  "Bağış Tutarı": "Donation Amount",
  "Aylık Bağış Tutarı": "Monthly Donation Amount",
  "Bağış tutarını sen belirlersin. Her katkı — büyüklüğü ne olursa olsun — bir cana dokunur.": "You choose the amount. Every contribution — no matter the size — touches a life.",
  "Mama Desteği de Ver": "Add a Meal Donation",
  "Bir de mama desteği: {name} gibi canların günlük beslenmesi için en hızlı katkı.": "How about a meal donation too — the fastest way to help feed companions like {name} every day.",
  "Bir de mama desteği: canların günlük beslenmesi için en hızlı katkı.": "How about a meal donation too — the fastest way to help feed our companions every day.",
  "Aylık desteğini kendin belirle. Her katkı — büyüklüğü ne olursa olsun — bir cana dokunur.": "You decide your monthly support. Every contribution — no matter the size — touches a life.",
  "Sadece rakam girin (örn. 5)": "Numbers only (e.g. 5)",
  "Yeni köpeği mevcut hayvanlarla nasıl tanıştırmayı planlıyorsunuz?": "How do you plan to introduce the new dog to your current pets?",
  "Yoğun dönemlerde dönüş": "Response during busy periods",
  "Yurt dışından başvuruyorum": "I'm applying from abroad",
  "Yüz yüze": "In-person",
  "Ziyaretler uygunluk durumuna göre randevuyla yapılır.": "Visits are by appointment based on availability.",
  "Zorlandığınız bir durum oldu mu? Nasıl çözdünüz?": "Have you faced a challenging situation? How did you resolve it?",
  "başkasına devredemezsiniz": "you cannot transfer it to someone else",
  "fotoğraf/video rica edilir.": "photos/videos are requested.",
  "gün": "days",
  "içinde dönüş yapılır.": "response time.",
  "sahiplenme sonrası 15 gün beklenir. 15 gün sonunda çip devri başlatılır.": "there is a 15-day waiting period after adoption. Microchip transfer starts after 15 days.",
  "seviyesi bilgisi; zamanla değişebilir).": "level info; may change over time).",
  "veya tam ziyaret seç": "or choose a full visit",
  "yapılır. Online görüşme çok nadir; sadece yurt dışı başvurularında veya güvenilir referans varsa mümkündür.": "in-person is standard. Online meetings are rare — only for overseas applicants or with a trusted reference.",
  "yüz yüze tanışma": "in-person meeting",
  "Çip devri hemen yapılmaz:": "Microchip transfer is not immediate:",
  "Çocukların yaşları": "Ages of children",
  "Ödeme Doğrulama": "Payment Verification",
  "Ödeme Yöntemi Seç": "Choose Payment Method",
  "Ödeme bildirimi gönderilemedi": "Payment notification couldn't be sent",
  "Ödeme eşleştirmesi için kısa bilgi.": "Short info for payment reconciliation.",
  "Ödeme sayfası alınamadı": "Couldn't load payment page",
  "Ödeme sayfası oluşturulamadı": "Couldn't create payment page",
  "Öncelik Ankara içidir.": "Priority is within Ankara.",
  "Öncelik ve şehir": "Priority & city",
  "Örn: 1 saat": "e.g. 1 hour",
  "Örn: 5, 8, 12": "e.g. 5, 8, 12",
  "Örn: Ailem bakacak / Pet otel": "e.g. Family will help / Pet hotel",
  "Örn: Ben ve eşim": "e.g. Me and my spouse",
  "Örn: Hafta içi 18:00-21:00, Hafta sonu her saat": "e.g. Weekdays 18:00-21:00, Weekends anytime",
  "Örn: Ziraat Bankası": "e.g. Ziraat Bank",
  "İade kuralı": "Return rule",
  "İletişim Bilgileri": "Contact Information",
  "İletişim ve Konum": "Contact & Location",
  "İlk 3 ay:": "First 3 months:",
  "İlk 3 gün:": "First 3 days:",
  "İlk 3 hafta:": "First 3 weeks:",
  "İlçe": "District",
  "İşlem başarısız": "Operation failed",
  "Şehir": "City",
  "Genel Bağış": "General Donation",
  "Aylık Destek Ol": "Support Monthly",
  "Her ay düzenli desteğinle yüzlerce can güvende kalsın.": "With your monthly support, hundreds of souls stay safe.",
  "{name}'nın karnını doyur.": "Feed {name}.",
  "{name}'nın sağlık kontrolüne katkı ol.": "Contribute to {name}'s health checkup.",
  "Tek seferlik destekle {name}'nın bakımına katkıda bulun. Kimsesiz canlara umut ol.": "Support {name}'s care with a one-time gift. Give hope to homeless animals.",
  "Devam et (Ödeme)": "Continue (Payment)",
  "{name} için Koruyucu Aile Ol": "Become a Guardian Family for {name}",
  "Koruyucu aile desteği maddidir (geçici yuva başvurusu değildir). Aylık desteğinle bakım bütçesine katkı olursun; biz de can dostunun güvenle bakıldığından emin oluruz.": "Guardian family support is financial (not a foster application). Your monthly gift covers care costs, and we ensure your companion stays safe.",
  "Koruyucu Aile Olarak:": "As a Guardian Family:",
  "Talep ederek fotoğraf/video ve durum güncellemeleri alabilirsiniz": "You can request photos, videos and status updates",
  "Sağlık ve önemli gelişmeler hakkında bilgilendirilebilirsiniz": "You'll be informed about health and important updates",
  "Desteğinizi istediğiniz zaman durdurabilirsiniz.": "You can stop your support anytime.",
  "gün mama": "days of food",
  "veteriner katkısı": "vet contribution",
  "veteriner ziyareti": "vet visit",
  "Genel bağış": "General donation",
  "Bu destek genel mama bütçesine katkı olur.": "This support contributes to the general food budget.",
  "Bu destek genel veteriner bütçesine katkı olur.": "This support contributes to the general vet budget.",
  "Bu destek can dostlarımızın bakımına gider.": "This support goes to the care of our companions.",
  "Bu destek {n} gün tok tutar.": "This support keeps them fed for {n} days.",
  "Bu katkı veteriner bakımına destek olur.": "This contribution supports vet care.",
  "Bu destek {n} veteriner ziyareti sağlar.": "This support covers {n} vet visits.",
  "Bağışın {name}'nın bakımına katkı olacak.": "Your donation will contribute to {name}'s care.",
  "{title} desteğiniz alındı. Teşekkürler!": "Your {title} support has been received. Thank you!",
  "{n} gün mama desteği sağladın.": "You provided {n} days of food support.",
  "Veteriner desteğin alındı.": "Your vet support has been received.",
  "{n} veteriner ziyareti desteği sağladın.": "You provided {n} vet visit support.",
  "Veya aylık tutar girin:": "Or enter a monthly amount:",
  "İşleniyor...": "Processing...",
  "Devam et": "Continue",
  "Vazgeç": "Cancel",
  "Ödenecek tutar:": "Amount to pay:",
  "Aylık tutar:": "Monthly amount:",
  "ay": "mo",
  "Ödeme Yaptım": "I've Paid",
  "Düzenli Ödeme (Fonzip)": "Recurring Payment (Fonzip)",
  "Kart ile Öde": "Pay by Card",
  "Fonzip'in güvenli sayfasında kart bilgilerinizi tanımlayacaksınız. Her ay otomatik tahsil edilir, dilediğiniz zaman iptal edebilirsiniz.": "You'll enter your card details on Fonzip's secure page. Charges are automatic each month; you can cancel anytime.",
  "Güvenli ödeme sayfasına yönlendirileceksiniz. Kart bilgileriniz bizde saklanmaz.": "You'll be redirected to a secure payment page. Your card details are not stored with us.",
  "Yönlendiriliyor...": "Redirecting...",
  "Fonzip ile Düzenli Öde →": "Pay Recurringly with Fonzip →",
  "Fonzip ödeme sayfası yeni sekmede açıldı": "The Fonzip payment page opened in a new tab",
  "Düzenli desteğin onaylandı 💛": "Your recurring support is confirmed 💛",
  "Fonzip'te \"Aylık\" seçeneğini işaretle, tutarını ve kart bilgilerini gir. Ödemen tamamlanınca bu ekran otomatik güncellenir.": "On Fonzip, select \"Monthly\", enter your amount and card details. This screen updates automatically once your payment completes.",
  "Fonzip'te \"Aylık\" seçeneğini işaretle, tutarını ve kart bilgilerini gir. Eşleştirme için aynı e-postayı kullan:": "On Fonzip, select \"Monthly\", enter your amount and card details. Use the same email so we can match it:",
  "Eşleştirmeyi garantiye almak için Fonzip formunda buradaki e-postanı kullan:": "To guarantee matching, use this email on the Fonzip form:",
  "KAHU Referans Kodun": "Your KAHU Reference Code",
  "Fonzip formunda \"Açıklama / Not\" alanı varsa bu kodu yazabilirsin (isteğe bağlı).": "If the Fonzip form has a \"Note\" field, you can paste this code (optional).",
  "Sayfa açılmadıysa buraya tıkla →": "If the page didn't open, click here →",
  "Ödemeyi Tamamladım": "I've Completed the Payment",
  "Fonzip sayfasını yeniden aç": "Reopen the Fonzip page",
  "Kopyalandı": "Copied",
  "Fonzip'in güvenli sayfası yeni sekmede açılır; \"Aylık\" seçeneğiyle kart bilgilerini tanımlarsın. Her ay otomatik tahsil edilir, dilediğin zaman iptal edebilirsin.": "Fonzip's secure page opens in a new tab; choose \"Monthly\" and enter your card details. Charged automatically each month; cancel anytime.",

  "Güvenli Ödeme Sayfasına Git": "Go to Secure Payment Page",
  "Ödeme başlatılamadı": "Couldn't start payment",
  "Geri (Ödeme Yöntemi Değiştir)": "Back (Change Payment Method)",
  "Daha sürdürülebilir etki için:": "For more sustainable impact:",
  "Minimum tutar {n}": "Minimum amount {n}",
  "Toplam:": "Total:",
  "{name} için Başvuru": "Application for {name}",
  "{name} için Koruyucu Aile": "Guardian Family for {name}",
  "Adım {n} / {total}": "Step {n} / {total}",
  "Başvuruyu Gönder": "Submit Application",
  "Aylık {amount} TL Destek Ol": "Support with {amount} TL Monthly",
  "Başvuruya Geç": "Continue to Application",
  "Detaylı Başvuruya Geç": "Continue to Detailed Application",
  "Devam": "Continue",
  "Genellikle": "Usually",
  "24 saat": "24 hours",
  "Genellikle 24 saat içinde dönüş olduğunu; yoğunlukta dönüşün 1 haftaya uzayabileceğini anladım.": "I understand that responses usually come within 24 hours; during busy periods, it may take up to 1 week.",
  "Randevusuz ziyaret kabul edilmediğini anladım.": "I understand that walk-in visits are not accepted.",
  "Devam etmek için \"Hayır\" seçeneğini işaretlemeniz gerekir.": "To continue, you must check the \"No\" option.",
  "1 veteriner ziyareti": "1 vet visit",
  "Yasal sorumluluk notu": "Legal responsibility note",
  "Pativista ceza kesmez.": "Pativista does not issue fines.",
  "Yukarıdaki bilgileri okudum ve anladım.": "I have read and understood the information above.",
  "Bakamazsam köpeği başkasına devredemeyeceğimi ve Pativista'ya iade edeceğimi anladım.": "I understand that if I can't care, I cannot transfer the dog to someone else, and must return them to Pativista.",
  "Pativista'nın ceza kesmediğini; ancak çipli hayvanın kaybı vb. durumlarda devletin cezaları olabileceğini (güncel bilgi 93.000 TL seviyesi; tutar değişebilir), anladım.": "I understand that Pativista does not issue fines, but the government may impose penalties in cases like the loss of a microchipped dog (current information ~93,000 TL level; the amount may change).",
  "1 haftaya": "1 week",
  "uzayabilir.": "may extend.",

  // ============ DonationFlow — additions ============
  "Ad Soyad": "Full Name",
  "Kopyala": "Copy",
  "Tutar": "Amount",
  "ziyaret": "visit",
  "Banka Havalesi / EFT (IBAN)": "Bank Transfer / EFT (IBAN)",
  "Banka uygulamandan transfer yapabilirsin.": "You can transfer from your banking app.",
  "{label} kopyalandı!": "{label} copied!",
  "{amount} ile Aylık Destek Ol": "Support Monthly with {amount}",
  "Ödemeniz kontrol edilince bağışınız onaylanacak.": "Your donation will be confirmed once your payment is verified.",
  "Dilersen gelişmeleri takip etmek için WhatsApp üzerinden güncelleme alabilirsin.": "If you like, you can receive updates via WhatsApp to follow the progress.",
  "ile devam edebilirsin.": "you can continue with this.",
  "IBAN": "IBAN",

  // ============ AdoptionForm & GuardianForm — additional strings ============
  "Semt (opsiyonel)": "Neighborhood (optional)",
  "Semt": "Neighborhood",
  "Ankara dışından sahiplendirme mümkündür; ancak öncelik Ankara içidir. Ankara dışı başvurular ikinci sırada değerlendirilir.": "Adoption from outside Ankara is possible; however priority is within Ankara. Applications from outside Ankara are evaluated second.",
  "Online görüşme istisnadır; sadece yurt dışı başvurularında veya güvenilir referans varsa mümkündür.": "Online meetings are the exception — only possible for applications from abroad or with a trusted reference.",
  "Bazı köpekler uzun süre yalnız kalmaya uyum sağlayamayabilir. Bu durum görüşmede netleştirilecektir.": "Some dogs may not adapt to being alone for long hours. This will be clarified during the interview.",
  "İzin konusu görüşmede netleşir. İzin alamayacaksanız süreç ilerlemeyebilir.": "The permission question will be clarified during the interview. If you can't secure permission, the process may not proceed.",
  "Ev içi uzlaşı olmadan süreç ilerlemesi zorlaşabilir.": "Without household agreement, the process may be difficult to move forward.",
  "Sağlık uyumu için bu durum görüşmede netleşmelidir.": "For health compatibility, this must be clarified during the interview.",
  "Telefon numarası zorunludur.": "Phone number is required.",
  "Geçerli bir telefon numarası girin (örn. 0532 123 45 67).": "Please enter a valid phone number (e.g. 0532 123 45 67).",
  "E-posta zorunludur.": "Email is required.",
  "Geçerli bir e-posta adresi girin (örn. ad@domain.com).": "Please enter a valid email (e.g. name@domain.com).",
  "Bilmiyorum": "I don't know",
  "Saat": "Hours",
  "Ziyaret": "Visits",
  "Kime Koruyucu Aile Olacağını Seç": "Choose Whom to Become a Guardian For",
  "Aylık taahhüdün başladı. Dilersen şu an tek seferlik bir katkıyla bugün {name} için sıcak bir tabak mama ya da bir veteriner ziyareti hediye edebilirsin.": "Your monthly commitment has begun. If you'd like, you can also make a one-time gift right now — a warm bowl of food or a vet visit for {name} today.",
  "{name} için Mama Desteği": "Food Support for {name}",
  "{name} için Veteriner Desteği": "Vet Support for {name}",
  "bir can": "a companion",
  "Belki de asıl niyetin farklı: {name} ile aynı çatının altında yaşamak…": "Perhaps your true heart is pointing elsewhere: sharing a home with {name}…",
  "Belki de asıl niyetin farklı: bir canı yüreğine alıp yuvana götürmek…": "Perhaps your true heart is pointing elsewhere: taking a soul into your home for good…",
  "Sahiplenmeden Rahat Edemeyeceğim": "I Won't Rest Until I Adopt",

  // ============ Event dynamic field values (fallback until admin translates via CMS GPT-5) ============
  "Pativista Sahiplendirme Günü": "Pativista Adoption Day",
  "KAHU destekli": "Powered by KAHU",
  "KAHU DESTEKLİ": "POWERED BY KAHU",
  "Kuğulu Park": "Kuğulu Park",
  "Kuğulu Park, Tunalı Hilmi Cd., Çankaya / Ankara": "Kuğulu Park, Tunalı Hilmi Cd., Çankaya / Ankara",
  "Bu Pazar Kuğulu Park'a bekliyoruz! Gel, yuva bulan canların neşesine ortak ol.": "This Sunday we'll be waiting at Kuğulu Park! Come along and share in the joy of companions finding their home.",
  "Veteriner Desteği": "Veterinary Support",
  "Mama Desteği": "Food Support",
  "Deneyim ve Motivasyon": "Experience and Motivation",
  "Tam Koruyucu Aile": "Full Guardian Family",
  "Paylı destek": "Shared support",
  "Telefon (WhatsApp)": "Phone (WhatsApp)",
  "(opsiyonel)": "(optional)",
  "ornek@email.com": "example@email.com",
  "{amount} TL/ay = Tam Koruyucu Aile": "{amount} TL/month = Full Guardian Family",
  "Minimum tutar {amount} TL'dir.": "Minimum amount is {amount} TL.",
  "Balkon": "Balcony",
  "Apartman": "Apartment",
  "Kendi evim": "My own home",
  "Kirada": "Renting",
  "Ev tipi": "Home type",
  "Ev durumu": "Housing status",
  "Ev sahibi hayvana izin veriyor mu?": "Does the landlord allow pets?",
  "Ekran {n}/8": "Screen {n}/8",
  "Evde kedi var": "There is a cat at home",
  "Kim ilgilenecek?": "Who will take care?",
  "Tatilde kim bakacak?": "Who will look after the dog during holidays?",
  "Neden sahiplenmek istiyorsunuz?": "Why do you want to adopt?",
  "Bazı apartman/site yönetim planlarında evcil hayvanla ilgili kısıtlar bulunabiliyor. Onay aşamasında yönetim planı veya bina yönetimi teyidi istenebilir. Kısıt varsa süreç ilerlemeyebilir.": "Some apartment/complex management plans may have pet-related restrictions. During approval, the management plan or building management confirmation may be requested. If restricted, the process may not proceed.",

  // ============ AdoptionForm — Steps 3-9 batch ============
  "25+ yaş tercihi vardır. Lütfen aşağıdaki soruları cevaplayın:": "There is a 25+ age preference. Please answer the questions below:",
  "Online görüşme için yurt dışı veya referans şartı gereklidir. Aksi halde yüz yüze seçeneğini tercih edin.": "Online meetings require an abroad application or a trusted reference. Otherwise please choose in-person.",
  "Tam adres bu aşamada istenmez. Görüşme aşamasında paylaşılacaktır.": "The full address is not requested at this stage. It will be shared during the interview.",
  "Alerji varsa ya da emin değilseniz, sağlığınız için onay aşamasında durumun netleşmesi istenebilir. Gerekirse alerji testi veya doktor görüşü talep edilebilir.": "If there's an allergy or you're not sure, we may ask to clarify during approval for your health. If needed, an allergy test or a doctor's opinion may be requested.",
  "Evde başka hayvan yok. Bu alanları boş bırakabilirsiniz.": "There is no other animal at home. You can leave these fields empty.",
  "Bu tercih görüşmede değerlendirilecektir. Bazı köpekler için ev içi yaşam daha uygun olabilir.": "This preference will be evaluated during the interview. Indoor life may be more suitable for some dogs.",
  "Bilgilendirme": "Information",
  "Not: Onay aşamasında bina yönetimi / yönetim planı teyidi istenebilir.": "Note: During approval, confirmation from building management / the management plan may be requested.",
  "Not: Onay aşamasında gerekirse alerji testi veya doktor görüşü istenebilir.": "Note: During approval, an allergy test or a doctor's opinion may be requested if needed.",
  "Başvuruyu göndermeden önce aşağıdaki maddeleri onaylamanız gerekmektedir:": "Before submitting your application, you must confirm the items below:",
  "Ankara dışındaysam önceliğin Ankara içi olduğunu anladım.": "If I'm outside Ankara, I understand that priority is within Ankara.",
  "Online görüşmenin istisna olduğunu (yurt dışı başvurusu veya güvenilir referans), anladım.": "I understand that online meetings are the exception (application from abroad or a trusted reference).",
  "Sahiplenme sonrası 15 gün beklenip çip devrinin sonra başlatıldığını, anladım.": "I understand that the chip transfer is started after a 15-day waiting period following adoption.",
  "Sahiplendirme sonrası 15 günde bir fotoğraf/video rica edileceğini, anladım.": "I understand that a photo/video will be requested every 15 days after adoption.",
  "Başvuru gönderilemedi": "The application could not be submitted",

  // ============ GuardianForm — batch ============
  "Teşekkürler! Ödemeniz kontrol edilince güncellemeleriniz başlayacak.": "Thank you! Your updates will start once your payment is verified.",
  "Teşekkürler! Koruyucu aile desteğiniz başladı.": "Thank you! Your Guardian Family support has begun.",
  "Güncellemeler (varsa) WhatsApp üzerinden iletilecek.": "Updates (if any) will be shared via WhatsApp.",
  "Düzenli destek için her ay aynı tutarı gönderebilir veya bankanızdan düzenli havale talimatı verebilirsiniz.": "For recurring support you can send the same amount each month or set up a standing transfer order with your bank.",
  "{name}'e Geri Dön": "Back to {name}",
  "Tam Koruyucu Aile: {amount} TL/ay.": "Full Guardian Family: {amount} TL/month.",
  "İstersen paylı destekle de katkı olabilirsin — her destek canlara dokunur": "If you like you can also share the support — every contribution touches lives",
  "Bankanızdan havale/EFT yapabilir veya her ay otomatik gitmesi için düzenli havale talimatı oluşturabilirsiniz.": "You can transfer/EFT from your bank or set up a standing transfer order so it goes automatically each month.",
  "Lütfen havale açıklamasına bu kodu yazın. Böylece ödemenizi hızlıca eşleştiririz.": "Please write this code in the transfer description so we can match your payment quickly.",
  "Düzenli destek için bankanızdan düzenli havale talimatı oluşturabilirsiniz.": "For recurring support you can set up a standing transfer order with your bank.",
  "Bu ay için ilk ödeme alınır. Aylık otomatik tahsilat Iyzico abonelik aktif olduğunda devreye girecek; o güne kadar ay başında kibarca hatırlatma e-postası göndereceğiz.": "The first payment is charged for this month. Automatic monthly billing will start once Iyzico subscription is active; until then we'll send a friendly reminder email at the start of each month.",
  "{amount} TL Fonzip ile Düzenli Öde": "Pay {amount} TL Recurring via Fonzip",
  "{amount} TL Iyzico ile Öde": "Pay {amount} TL via Iyzico",
  "kadim Hawaii kültüründe": "in ancient Hawaiian culture means",
  "koruyucu, kollayıcı, emanetçi": "guardian, keeper, one entrusted with care",
  " demektir. Buradan ilhamla: köpekler ve insanlar birbirinin sahibi değil, birbirinin ": ". That is our inspiration: dogs and humans aren't each other's owners — they're each other's ",
  ". Bu platform, o karşılıklı emanetin dijital karşılığı.": ". This platform is the digital form of that mutual trust.",
};

/**
 * Locale-aware string helper.
 * useT() → returns a function t(trText) that:
 *   - Turkish mode: returns trText unchanged
 *   - English mode: returns HARDCODED_EN[trText] if exists, else trText (fallback)
 */
export function useT() {
  const { locale } = useLanguage();
  return (trText) => {
    if (locale !== "en") return trText;
    return HARDCODED_EN[trText] || trText;
  };
}
